import { useCallback } from "react";
import type { FinanceOptionType } from "../../../common-deprecated/types/CommonTypes";
import {
    type SliderButtonType,
    BUTTON_MAX,
    BUTTON_MIN,
} from "../../../shared-logic/features/filters/utils/constants/filterConfigConstants";
import { sanitize } from "../../../common-deprecated/utils/sanitizer";

type usePriceFilterSliderReturnType = {
    clippedMin: number;
    clippedMax: number;
    setValueFn: (newValue: number, type: SliderButtonType) => void;
    getValueLabelFn: (secondary: boolean) => (value: number) => SanitizedString;
    getSecondaryValues: (minValue: number, maxValue: number) => { min: string; max: string } | undefined;
};

const usePriceFilterSlider = (
    minValue: number,
    maxValue: number,
    customMinValue: number,
    customMaxValue: number,
    valueLabel: string,
    setValue: (type: "max" | "min", value: number, financeOption: FinanceOptionType) => void,
    activeFinanceOption: FinanceOptionType,
    formatPrice: (price: number, secondary: boolean) => string,
): usePriceFilterSliderReturnType => {
    const clippedMin = customMinValue || minValue;
    const clippedMax = customMaxValue || maxValue;

    const setValueFn = useCallback(
        (newValue: number, type: SliderButtonType) => {
            if (type === BUTTON_MAX) {
                setValue("max", newValue === clippedMax ? maxValue : newValue, activeFinanceOption);
            }
            if (type === BUTTON_MIN) {
                setValue("min", newValue === clippedMin ? minValue : newValue, activeFinanceOption);
            }
        },
        [activeFinanceOption, clippedMax, clippedMin, maxValue, minValue, setValue],
    );

    const getValueLabelFn = useCallback(
        (secondary: boolean) =>
            (value: number): SanitizedString => {
                // valueLabel has two possible formats:
                // - "{min} - {max}" for range labels
                // - "{value}" for single value labels
                // We need to replace the placeholders with the formatted price.
                // If the valueLabel contains "{min}" or "{max}", we assume it's a range label.
                // If it contains "{value}", we assume it's a single value label.
                const stringValue = valueLabel
                    .replace("{min}", formatPrice(clippedMin, secondary))
                    .replace("{max}", formatPrice(clippedMax, secondary))
                    .replace("{value}", formatPrice(value, secondary))
                    .replace(/&nbsp;/g, " "); // Replace non-breaking spaces with regular spaces

                return sanitize(stringValue);
            },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [valueLabel],
    );

    const getSecondaryValues = useCallback(
        (currentMinValue: number, currentMaxValue: number) => {
            try {
                return {
                    min: formatPrice(currentMinValue, true),
                    max: formatPrice(currentMaxValue, true),
                };
            } catch {
                return undefined;
            }
        },
        [formatPrice],
    );

    return { clippedMin, clippedMax, setValueFn, getValueLabelFn, getSecondaryValues };
};
export default usePriceFilterSlider;
